import React, {useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Container,
    Paper,
    TextField,
    AppBar,
    Toolbar,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Menu
} from '@mui/material';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import Loading from "../Loading";

const WaiterPanel = () => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [token, setToken] = useState(localStorage.getItem("tokenWaiter") || null);
    const [userId, setUserId] = useState(null);
    const [restaurantId, setRestaurantId] = useState(null);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [orders, setOrders] = useState([]);
    const [takenOrders, setTakenOrders] = useState([]);
    const [client, setClient] = useState(null);
    const [dialogs, setDialogs] = useState([]);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [confirmPaymentDialogOpen, setConfirmPaymentDialogOpen] = useState(false);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null); // Anchor dla menu
    const [closeOrderDialogOpen, setCloseOrderDialogOpen] = useState(false);
    const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
    const [selectedComments, setSelectedComments] = useState([]);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const soundEnabledRef = useRef(soundEnabled);
    const audioRef = useRef(new Audio('/sounds/notification.wav'));
    const [loading, setLoading] = useState(false);

    const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL;

    useEffect(() => {
        soundEnabledRef.current = soundEnabled;
    }, [soundEnabled]);

    const playSound = () => {
        if (soundEnabledRef.current) {
            const audio = audioRef.current;
            audio.currentTime = 0;
            audio.play().catch(err => console.warn('Audio play failed:', err));
        }
    };

    useEffect(() => {
        const storedToken = localStorage.getItem("tokenWaiter");
        const storedWaiterId = localStorage.getItem("idWaiter");
        const storedRestaurantId = localStorage.getItem("idRestaurant");
        if (storedToken) {
            setIsLoggedIn(true);
            setToken(storedToken);
            setUserId(storedWaiterId);
            setRestaurantId(storedRestaurantId);
        }

        const interceptor = axios.interceptors.response.use(
            response => response,
            error => {
                if (error.response) {
                    if (error.response.status === 403) {
                        handleLogout();
                    }
                } else if (error.request) {
                    console.error("Błąd sieciowy:", error.request);
                    handleLogout();
                } else {
                    console.error("Błąd:", error.message);
                    handleLogout();
                }
                return Promise.reject(error);
            }
        );
        return () => {
            axios.interceptors.response.eject(interceptor);
        };
    }, []);

    useEffect(() => {
        const storedToken = localStorage.getItem("tokenWaiter");
        const storedWaiterId = localStorage.getItem("idWaiter");
        const storedRestaurantId = localStorage.getItem("idRestaurant");
        if (storedToken) {
            setIsLoggedIn(true);
            setToken(storedToken);
            setUserId(storedWaiterId);
            setRestaurantId(storedRestaurantId);
        }
    }, []);

    // Mapowanie metod płatności
    const mapPaymentMethod = (method) => {
        switch (method) {
            case 'WAITER_CARD':
                return 'karta';
            case 'WAITER_CASH':
                return 'gotówka';
            default:
                return method;
        }
    };

    // Funkcja pobierająca zaległe aktualizacje
    const fetchUpdates = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${baseUrl}/api/waiter/getUpdates`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const updates = response.data;
            if (updates && updates.length > 0) {
                updates.forEach(update => {
                    handleUpdateInformation(update);
                });
            }
        } catch (error) {
            console.error('Błąd podczas pobierania zaległych aktualizacji:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateInformation = (data) => {
        playSound();
        let message = '';
        let title = 'Nowa informacja';
        let totalAmount = 0;

        if (data.updateOrder) {
            title = '🛎️ Aktualizacja zamówienia';
            message += `Stolik nr ${data.tableNumber} zamówił:\n`;
            data.updateOrder.forEach(item => {
                message += `- ${item.name} | Ilość: ${item.quantity}\n`;
            });
            if(data.comment){
                message += `\n⚠️️Uwagi: ${data.comment}\n`;
            }
        }

        if (data.payment) {
            title = '💳 Płatność';
            message += `Klient ze stolika nr ${data.tableNumber} chce opłacić zamówienie:\n`;

            if (data.payment.items && data.payment.items.length > 0) {
                message += `🧾 Pozycje do zapłaty:\n`;
                data.payment.items.forEach(item => {
                    message += `-  ${item.name}  ${item.quantity} x ${item.price} PLN = ${item.totalPrice} PLN\n`;
                    totalAmount += item.totalPrice;
                });
            }
            message += `\nMetoda płatności: ${mapPaymentMethod(data.payment.paymentMethod)}\n`;
            if (data.payment.isNip && data.payment.nip) {
                message += `🧾 NIP do faktury: ${data.payment.nip}\n`;
            }
            if (data.payment.tipForAppDeveloper && parseFloat(data.payment.tipForAppDeveloper) > 0) {
                message += `💙 Napiwek dla dewelopera: ${data.payment.tipForAppDeveloper} PLN\n`;
                totalAmount += parseFloat(data.payment.tipForAppDeveloper);
            }
            if (data.payment.tipForWaiter && parseFloat(data.payment.tipForWaiter) > 0) {
                message += `🤝 Napiwek dla kelnera: ${data.payment.tipForWaiter} PLN\n`;
                totalAmount += parseFloat(data.payment.tipForWaiter);
            }

            message += `\n💵 Suma do zapłaty: ${totalAmount.toFixed(2)} PLN\n`;
        }

        if (data.waiterAttention) {
            title = '🚨 Wezwanie kelnera';
            message += `Uwaga! Stolik nr ${data.tableNumber} wymaga obsługi kelnera.`;
        }

        if (message) {
            const newDialog = {
                id: data.id,
                title: title,
                message: message,
                open: true
            };
            setDialogs((prevDialogs) => [...prevDialogs, newDialog]);
        }
    };

    const handleCloseDialog = async (id) => {
        try {
            await axios.post(
                `${baseUrl}/api/waiter/acceptUpdate`,
                null,
                {
                    params: { id },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
        } catch (error) {
            console.error('Błąd podczas aktualizacji statusu:', error);
        } finally {
            setDialogs((prevDialogs) => prevDialogs.filter(dialog => dialog.id !== id));
        }
    };

    const handleCloseDialogFromWs = (id) => {
        setDialogs((prevDialogs) => prevDialogs.filter(dialog => dialog.id !== Number(id)));
    };

    // Inicjalizacja połączenia WebSocket
    const initializeWebSocket = () => {
        const socket = new SockJS(`${baseUrl}/ws`);
        const stompClient = new Client({
            webSocketFactory: () => socket,
            reconnectDelay: 5000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
            connectHeaders: {
                Authorization: `Bearer ${token}`,
            },
            onConnect: () => {
                stompClient.publish({ destination: "/app/orders" });
                stompClient.subscribe("/topic/newOrders/" + restaurantId, (message) => {
                    const newOrders = JSON.parse(message.body);
                    setOrders(prevOrders => {
                        if (newOrders.length > prevOrders.length) {
                            playSound();
                        }
                        return newOrders;
                    });
                });
                stompClient.subscribe("/topic/takenOrders/" + restaurantId + "/" + userId, (message) =>
                    setTakenOrders(JSON.parse(message.body))
                );
                stompClient.subscribe("/topic/updateInformation/" + restaurantId + "/" + userId, (message) =>
                    handleUpdateInformation(JSON.parse(message.body))
                );
                stompClient.subscribe("/topic/callToWaiter/" + restaurantId, (message) =>
                    handleUpdateInformation(JSON.parse(message.body))
                );
                stompClient.subscribe("/topic/cancelCallToWaiter/" + restaurantId, (message) =>
                    handleCloseDialogFromWs(message.body)
                );
            },
            onWebSocketClose: (evt) => {
                setClient(null);
                setTimeout(() => {
                    initializeWebSocket();
                }, 5000);
            },
            onStompError: (frame) => {
                console.error("Błąd STOMP:", frame);
            }
        });

        stompClient.activate();
        setClient(stompClient);
    };

    useEffect(() => {
        if (!isLoggedIn || !token) {
            return;
        }
        fetchUpdates();
        initializeWebSocket();

        return () => {
            if (client) {
                client.deactivate();
                setClient(null);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, token]);

    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(`${baseUrl}/api/auth/login`, {
                username,
                password,
                role: 'ROLE_WAITER',
            });
            setToken(response.data.jwt);
            localStorage.setItem('tokenWaiter', response.data.jwt);
            setUserId(response.data.id);
            localStorage.setItem('idWaiter', response.data.id);
            setRestaurantId(response.data.restaurantId);
            localStorage.setItem('idRestaurant', response.data.restaurantId);
            setIsLoggedIn(true);
            setUsername('')
            setPassword('')
        } catch (error) {
            alert('Nieprawidłowe dane logowania');
        }
    };

    const handleLogout = () => {
        if (client) client.deactivate();
        setIsLoggedIn(false);
        setToken(null);
        setUserId(null);
        setRestaurantId(null);
        localStorage.removeItem('tokenWaiter');
    };

    const takeOrder = async (orderId) => {
        try {
            await axios.post(
                `${baseUrl}/api/waiter/takeOrder`,
                null,
                {
                    params: { orderId },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
        } catch (error) {
            console.error('Błąd podczas podejmowania zamówienia:', error);
        }
    };

    const handleOpenConfirmPaymentListDialog = (order) => {
        setSelectedOrder(order);
        setConfirmPaymentDialogOpen(true);
    };

    const handleCloseConfirmPaymentListDialog = () => {
        setConfirmPaymentDialogOpen(false);
        setSelectedOrder(null);
        setSelectedPayment(null);
    };

    const handleConfirmPayment = async () => {
        if (!selectedPayment) return;

        try {
            await axios.post(
                `${baseUrl}/api/waiter/confirmPayment`,
                null,
                {
                    params: { id: selectedPayment.id },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            handleCloseConfirmPaymentListDialog();
        } catch (error) {
            console.error('Błąd podczas potwierdzania płatności:', error);
        }
    };

    const getPaymentDescription = (payment) => {
        let description = `Metoda płatności: ${mapPaymentMethod(payment.paymentMethod)}\n`;
        description += `Pozycje do opłacenia:\n`;
        payment.items.forEach(item => {
            description += `- ${item.name} (${item.quantity} x ${item.price} PLN)\n`;
        });
        if (payment.tipForAppDeveloper) {
            description += `Napiwek dla dewelopera: ${payment.tipForAppDeveloper} PLN\n`;
        }
        if (payment.tipForWaiter) {
            description += `Napiwek dla kelnera: ${payment.tipForWaiter} PLN\n`;
        }
        if (payment.isNip && payment.nip) {
            description += `NIP do faktury: ${payment.nip}\n`;
        }
        return description;
    };

    const handleMenuOpen = (event, order) => {
        setAnchorEl(event.currentTarget);
        setSelectedOrder(order);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleOpenPaymentListDialog = () => {
        setPaymentDialogOpen(true);
        handleMenuClose();
    };

    const handleOpenCloseOrderDialog = () => {
        setCloseOrderDialogOpen(true);
        handleMenuClose();
    };

    const handleCloseCloseOrderDialog = () => {
        setCloseOrderDialogOpen(false);
    };

    const handleCloseOrder = async () => {
        if (!selectedOrder) return;

        try {
            await axios.post(
                `${baseUrl}/api/waiter/closeOrder`,
                null,
                {
                    params: { id: selectedOrder.id },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            handleCloseCloseOrderDialog();
        } catch (error) {
            console.error('Błąd podczas zamykania zamówienia:', error);
        }
    };

    // Funkcja otwierająca dialog z komentarzami
    const handleOpenCommentsDialog = (comments) => {
        setSelectedComments(comments);
        setCommentsDialogOpen(true);
    };

    if (!isLoggedIn) {
        return (
            <Container maxWidth="xs">
                <Paper elevation={3} sx={{ padding: 3, marginTop: 5 }}>
                    <Typography variant="h4" align="center" sx={{ marginBottom: 2 }}>
                        Logowanie
                    </Typography>
                    <form onSubmit={handleLogin}>
                        <TextField
                            label="Nazwa użytkownika"
                            variant="outlined"
                            fullWidth
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            sx={{ marginBottom: 2 }}
                        />
                        <TextField
                            label="Hasło"
                            type="password"
                            variant="outlined"
                            fullWidth
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            sx={{ marginBottom: 2 }}
                        />
                        <Button type="submit" variant="contained" color="primary" fullWidth>
                            Zaloguj się
                        </Button>
                    </form>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ padding: 0 }}>
            <Loading open={loading} />
            <AppBar position="fixed">
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="h6">Panel Kelnera</Typography>
                    <Box>
                        <IconButton
                            color="inherit"
                            onClick={() => setSoundEnabled(on => !on)}
                        >
                            {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
                        </IconButton>
                        <IconButton color="inherit" onClick={handleLogout}>
                            <ExitToAppIcon />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>
            <Box mt={12}>
                <Typography variant="h5" sx={{ marginTop: 4, marginBottom: 2 }}>Nowe zamówienia</Typography>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID Zamówienia</TableCell>
                                <TableCell>Stół</TableCell>
                                <TableCell>Pozycje zamówienia</TableCell>
                                <TableCell>Suma</TableCell>
                                <TableCell>Akcja</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.map((order) => (
                                <TableRow key={order.id}>
                                    <TableCell>{order.id}</TableCell>
                                    <TableCell>{order.tableNumber}</TableCell>
                                    <TableCell>
                                        {order.orderItems.map((item) => (
                                            <div key={item.id}>
                                                {item.name} ({item.quantity} x {item.price} PLN)
                                            </div>
                                        ))}
                                    </TableCell>
                                    <TableCell>
                                        {order.orderItems
                                            .reduce((total, item) => total + item.totalPrice, 0)
                                            .toFixed(2)} PLN
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => takeOrder(order.id)}
                                        >
                                            Podejmij
                                        </Button>
                                        {order.comments && order.comments.length > 0 && (
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                sx={{ ml: 1 }}
                                                onClick={() => handleOpenCommentsDialog(order.comments)}
                                            >
                                                Uwagi
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <Typography variant="h5" sx={{ marginTop: 4, marginBottom: 2 }}>Podjęte zamówienia</Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID Zamówienia</TableCell>
                            <TableCell>Stół</TableCell>
                            <TableCell>Pozycje zamówienia</TableCell>
                            <TableCell>Nieopłacone pozycje</TableCell>
                            <TableCell>Suma</TableCell>
                            <TableCell>Akcja</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {takenOrders.map((order) => (
                            <TableRow key={order.id}>
                                <TableCell>{order.id}</TableCell>
                                <TableCell>{order.tableNumber}</TableCell>
                                <TableCell>
                                    {order.orderItems.map((item) => (
                                        <div key={item.id}>
                                            {item.name} ({item.quantity} x {item.price} PLN)
                                        </div>
                                    ))}
                                </TableCell>
                                <TableCell>
                                    {order.unpaidItems &&
                                        order.unpaidItems.map((item) => (
                                            <div key={item.id}>
                                                {item.name} ({item.quantity} x {item.price} PLN)
                                            </div>
                                        ))}
                                </TableCell>
                                <TableCell>
                                    {order.orderItems
                                        .reduce((total, item) => total + item.totalPrice, 0)
                                        .toFixed(2)} PLN
                                </TableCell>
                                <TableCell>
                                    {order.payments && order.payments.some(payment => payment.status === 'NEW') && (
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => handleOpenConfirmPaymentListDialog(order)}
                                        >
                                            Potwierdź płatność
                                        </Button>
                                    )}
                                    {/* Ikona dla komentarzy – wyświetlana na wierzchu */}
                                    {order.comments && order.comments.length > 0 && (
                                        <IconButton
                                            onClick={() => handleOpenCommentsDialog(order.comments)}
                                            size="small"
                                        >
                                            <ChatBubbleOutlineIcon />
                                        </IconButton>
                                    )}
                                    <IconButton onClick={(e) => handleMenuOpen(e, order)} size="small">
                                        <MoreVertIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleOpenPaymentListDialog}>Wyświetl płatności</MenuItem>
                <MenuItem onClick={handleOpenCloseOrderDialog}>Zamknij zamówienie</MenuItem>
            </Menu>

            <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)}>
                <DialogTitle>Płatności dla zamówienia {selectedOrder?.id}</DialogTitle>
                <DialogContent>
                    <List>
                        {selectedOrder && selectedOrder.payments.map((payment) => (
                            <ListItem key={payment.id}>
                                <ListItemText
                                    primary={`Płatność ID: ${payment.id}`}
                                    secondary={
                                        <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                                            {getPaymentDescription(payment)}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPaymentDialogOpen(false)} color="primary">Zamknij</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={closeOrderDialogOpen} onClose={handleCloseCloseOrderDialog}>
                <DialogTitle>Czy na pewno chcesz zamknąć zamówienie?</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Zamknięcie zamówienia spowoduje zakończenie obsługi stolika.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCloseOrderDialog} color="primary">Nie</Button>
                    <Button onClick={handleCloseOrder} color="primary">Tak</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmPaymentDialogOpen} onClose={handleCloseConfirmPaymentListDialog}>
                <DialogTitle>Wybierz płatność do potwierdzenia</DialogTitle>
                <DialogContent>
                    <List>
                        {selectedOrder && selectedOrder.payments
                            .filter(payment => payment.status === 'NEW')
                            .map((payment) => (
                                <ListItem button key={payment.id} onClick={() => setSelectedPayment(payment)}>
                                    <ListItemText
                                        primary={`Płatność ID: ${payment.id}`}
                                        secondary={
                                            <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                                                {getPaymentDescription(payment)}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmPaymentListDialog} color="primary">Anuluj</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!selectedPayment} onClose={() => setSelectedPayment(null)}>
                <DialogTitle>Czy na pewno chcesz potwierdzić tę płatność?</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                        {selectedPayment && getPaymentDescription(selectedPayment)}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedPayment(null)} color="primary">Nie</Button>
                    <Button onClick={handleConfirmPayment} color="primary">Tak</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog z komentarzami */}
            <Dialog open={commentsDialogOpen} onClose={() => setCommentsDialogOpen(false)}>
                <DialogTitle>Uwagi</DialogTitle>
                <DialogContent>
                    <List>
                        {selectedComments.map((comment, index) => (
                            <ListItem key={index}>
                                <ListItemText primary={comment} />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCommentsDialogOpen(false)} color="primary">
                        Zamknij
                    </Button>
                </DialogActions>
            </Dialog>

            {dialogs.map((dialog) => (
                <Dialog key={dialog.id} open={dialog.open} onClose={() => handleCloseDialog(dialog.id)}>
                    <DialogTitle>{dialog.title}</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                            {dialog.message}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => handleCloseDialog(dialog.id)} color="primary">OK</Button>
                    </DialogActions>
                </Dialog>
            ))}
        </Container>
    );
};

export default WaiterPanel;